import React from "react";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";

const SectionHead = ({ children, t }) => (
  <Typography sx={{
    fontFamily: "'DM Mono', monospace",
    fontSize: "9px", letterSpacing: "0.14em",
    textTransform: "uppercase", color: t.text3,
    borderTop: `1px solid ${t.border}`,
    paddingTop: "14px", marginTop: "14px", marginBottom: "10px",
  }}>
    {children}
  </Typography>
);

const MACChip = ({ mac, active, onClick, t }) => (
  <Box
    onClick={onClick}
    sx={{
      padding: "5px 12px", borderRadius: "3px",
      cursor: "pointer", transition: "all 0.15s",
      fontSize: "11.5px", fontFamily: "'Inter', sans-serif",
      border: active ? `1px solid ${t.accent}55` : `1px solid ${t.border2}`,
      background: active ? t.accent2 : t.surface2,
      color: active ? t.accent : t.text2,
      "&:hover": { borderColor: t.accent + "44", color: t.accent },
    }}
  >
    {mac.macName}
  </Box>
);

const SOWChip = ({ sow, active, onClick, t }) => (
  <Box
    onClick={onClick}
    sx={{
      padding: "5px 12px", borderRadius: "3px",
      cursor: "pointer", transition: "all 0.15s",
      fontSize: "11.5px", fontFamily: "'Inter', sans-serif",
      border: active ? `1px solid ${t.accent}55` : `1px solid ${t.border2}`,
      background: active ? t.accent2 : t.surface2,
      color: active ? t.accent : t.text2,
      "&:hover": { borderColor: t.accent + "44", color: t.accent },
    }}
  >
    {sow.sowName}
  </Box>
);

const CapIndicator = ({ sowName, t, isDark }) => (
  <Box sx={{
    background: isDark ? "rgba(40,179,180,0.07)" : "rgba(3,81,77,0.05)",
    border: `1px solid ${t.border2}`,
    borderRadius: "3px", padding: "8px 12px", mb: "14px",
  }}>
    <Typography sx={{
      fontFamily: "'DM Mono', monospace",
      fontSize: "8px", letterSpacing: "0.08em",
      textTransform: "uppercase", color: t.accent, mb: "6px",
    }}>
      Billed Cap · {sowName}
    </Typography>
    <Typography sx={{
      fontFamily: "'Inter', sans-serif",
      fontSize: "11px", color: t.text2,
    }}>
      Position will be validated against SOW headcount cap on save
    </Typography>
  </Box>
);

const MACSOWSelector = ({
  macs,
  selectedMac,
  selectedSow,
  positionType,
  sowError,
  disabled,
  onSelectMac,
  onSelectSow,
  headcountCap,
  ublPoolStatus,
}) => {
  const theme  = useTheme();
  const t      = theme.custom;
  const isDark = theme.palette.mode === "dark";

  const sowsForMac     = selectedMac ? (macs.find((m) => m.macId === selectedMac)?.sows || []) : [];
  const selectedSowObj = sowsForMac.find((s) => s.sowId === selectedSow);

  return (
    <Box sx={{ pointerEvents: disabled ? "none" : "auto" }}>

      {/* MAC chips */}
      <SectionHead t={t}>MAC</SectionHead>
      <Box sx={{ display: "flex", gap: "6px", flexWrap: "wrap", mb: "12px" }}>
        {macs.map((mac) => (
          <MACChip
            key={mac.macId}
            mac={mac}
            active={selectedMac === mac.macId}
            onClick={() => onSelectMac(mac.macId)}
            t={t}
          />
        ))}
      </Box>

      {/* SOW chips — only visible once a MAC is selected */}
      {selectedMac && (
        <>
          <SectionHead t={t}>SOW / Project</SectionHead>

          {sowError && (
            <Typography sx={{
              fontSize: "10px", color: "#c84040",
              mb: "6px", fontFamily: "'DM Mono', monospace",
            }}>
              {sowError}
            </Typography>
          )}

          <Box sx={{ display: "flex", gap: "6px", flexWrap: "wrap", mb: "12px" }}>
            {sowsForMac.map((sow) => (
              <SOWChip
                key={sow.sowId}
                sow={sow}
                active={selectedSow === sow.sowId}
                onClick={() => onSelectSow(sow.sowId)}
                t={t}
              />
            ))}
          </Box>

          {/* Cap indicator — only for BIL after SOW picked */}
          {selectedSowObj && positionType === "BIL" && (
            <CapIndicator sowName={selectedSowObj.sowName} t={t} isDark={isDark} />
          )}

         {selectedSow && headcountCap !== null && positionType === "BIL" && (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: "6px",
      mt: "8px",
      padding: "6px 10px",
      borderRadius: "4px",
      background: "rgba(255,255,255,0.04)",
      border: `1px solid ${t.border}`,
    }}
  >
    <Typography
      sx={{
        fontFamily: "'DM Mono', monospace",
        fontSize: "9px",
        letterSpacing: "0.14em",
        textTransform: "uppercase",
        color: t.text3,
      }}
    >
      Headcount Cap
    </Typography>
    <Typography
      sx={{
        fontFamily: "'DM Mono', monospace",
        fontSize: "11px",
        fontWeight: 600,
        color: t.text1,
        marginLeft: "auto",
      }}
    >
      {headcountCap}
    </Typography>
  </Box>
)}

{positionType === "UBL" && ublPoolStatus && (
  <Box
    sx={{
      mt: "8px",
      padding: "8px 12px",
      borderRadius: "4px",
      background: "rgba(40,179,180,0.06)",
      border: `1px solid ${t.border}`,
    }}
  >
    <Typography
      sx={{
        fontFamily: "'DM Mono', monospace",
        fontSize: "8px",
        letterSpacing: "0.14em",
        textTransform: "uppercase",
        color: t.accent,
        mb: "8px",
      }}
    >
      UBL Pool · {ublPoolStatus.quarter}
    </Typography>

    <Box sx={{ display: "flex", gap: "6px" }}>
      {[
        { label: "Ceiling", value: ublPoolStatus.ceiling },
        { label: "Alloc to MAC", value: ublPoolStatus.deployed, dimColor: "#c87840" },
        { label: "SCG Pool", value: ublPoolStatus.bench, dimColor: "#c87840" },
        { label: "Remaining", value: ublPoolStatus.available, highlight: true },
      ].map(({ label, value, dimColor, highlight }) => (
        <Box
          key={label}
          sx={{
            flex: 1,
            padding: "6px 8px",
            borderRadius: "3px",
            background: highlight ? "rgba(40,179,180,0.10)" : "rgba(255,255,255,0.03)",
            border: `1px solid ${highlight ? t.accent + "55" : t.border}`,
            textAlign: "center",
          }}
        >
          <Typography
            sx={{
              fontFamily: "'DM Mono', monospace",
              fontSize: "15px",
              fontWeight: 700,
              color: highlight ? t.accent : dimColor ?? t.text,
              lineHeight: 1,
            }}
          >
            {value ?? "—"}
          </Typography>
          <Typography
            sx={{
              fontFamily: "'DM Mono', monospace",
              fontSize: "7.5px",
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: t.text3,
              mt: "3px",
            }}
          >
            {label}
          </Typography>
        </Box>
      ))}
    </Box>
  </Box>
)}
        </>
      )}

    </Box>
  );
};

export default MACSOWSelector;